import { resolveId } from '@medplum/core'
import type { Patient, Reference } from '@medplum/fhirtypes'
import {
  type DateString,
  endOfDateInLocalTimezone,
  parseDateString,
  startOfDateInLocalTimezone,
  toDateString,
  today,
} from '@seen/core/dates'
import { ExtendedAllergy } from '@seen/core/models/allergies'
import type { ExtendedAppointment } from '@seen/core/models/appointments/appointment'
import { Participant } from '@seen/core/models/patients/participant'
import {
  Badge,
  Button,
  cn,
  DotSeparator,
  MiniCalendar,
  MiniCalendarDay,
  MiniCalendarDays,
  MiniCalendarNavigation,
  TypographySubtle,
} from '@seen/ui'
import { IconPrinter, IconVirus } from '@tabler/icons-react'
import { compact, groupBy, sortBy } from 'lodash-es'
import pluralize from 'pluralize'
import { type JSX, useCallback, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router'
import { AppointmentDisplay } from '#src/components/appointment/AppointmentDisplay.tsx'
import { Avatar } from '#src/components/core/Avatar/Avatar.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
import { PatientName, TextLink } from '#src/components/index.ts'
import { ParticipantFlagBadge } from '#src/components/ParticipantFlagBadge.tsx'
import { useAppointments } from '#src/hooks/useAppointments.ts'
import { useHashMatch } from '#src/hooks/useHashMatch.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { useScrollHighlight } from '#src/hooks/useScrollHighlight.ts'
import { useSearch } from '#src/hooks/useSearch.ts'
import { DayCenterDayNote } from '#src/pages/participants/DayCenterDayNote.tsx'

function isAppointmentInPeriod(
  appointment: ExtendedAppointment,
  period: 'am' | 'pm'
): boolean {
  if (!appointment.start) {
    return false
  }
  const hour = new Date(appointment.start).getHours()
  return period === 'am' ? hour < 12 : hour >= 12
}

export function DayCenterAttendance({
  period,
}: {
  period: 'am' | 'pm'
}): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams()

  const [selectedDate, setSelectedDate] = useState<DateString | null>(
    searchParams.get('date') ?? today()
  )

  const handleDateChange = useCallback(
    (date: Date | undefined) => {
      const dateString = date ? toDateString(date) : null
      setSelectedDate(dateString)
      setSearchParams((params) => {
        if (dateString) {
          params.set('date', dateString)
        } else {
          params.delete('date')
        }
        return params
      })
    },
    [setSearchParams]
  )

  const selectedDateAsDate = useMemo(
    () => (selectedDate ? parseDateString(selectedDate) : undefined),
    [selectedDate]
  )

  const { data: appointments } = useAppointments({
    start: startOfDateInLocalTimezone(selectedDate ?? today()),
    end: endOfDateInLocalTimezone(selectedDate ?? today()),
    statuses: ['booked'],
  })

  const sortedAppointments = useMemo(
    () =>
      sortBy(
        appointments?.filter((a) => !!a.patient) ?? [],
        (appointment) => appointment.start
      ),
    [appointments]
  )

  const appointmentsByParticipantId = useMemo(() => {
    const grouped = groupBy(
      sortedAppointments?.filter((a) => !!a.patient) ?? [],
      (appointment) => resolveId(appointment.patient)
    )

    // Only keep participants with at least one Day Center appointment
    // in the selected period (AM: starts before noon, PM: starts at/after noon).
    return Object.fromEntries(
      Object.entries(grouped).filter(([_, appointments]) =>
        appointments.some(
          (appointment) =>
            appointment.isDayCenter &&
            isAppointmentInPeriod(appointment, period)
        )
      )
    )
  }, [sortedAppointments, period])

  const { data: participants } = useSearch(Participant, {
    _id: Object.keys(appointmentsByParticipantId),
    _sort: 'family',
  })

  const numParticipants = Object.keys(appointmentsByParticipantId).length

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MiniCalendar
            value={selectedDateAsDate}
            onValueChange={handleDateChange}
            days={5}
            weekdaysOnly
          >
            <MiniCalendarNavigation direction="prev" />
            <MiniCalendarDays>
              {(date) => (
                <MiniCalendarDay key={date.toISOString()} date={date} />
              )}
            </MiniCalendarDays>
            <MiniCalendarNavigation direction="next" />
          </MiniCalendar>
          <div className="text-2xs whitespace-nowrap text-foreground-secondary">
            {numParticipants} {pluralize('participant', numParticipants)}
          </div>
        </div>
        <Button
          className="gap-2 print:hidden"
          onClick={() => window.print()}
          variant="outline"
        >
          <IconPrinter className="size-4" />
          Print
        </Button>
      </div>
      {/* {numParticipants > 0 && (
        <div className="text-sm text-muted-foreground">
          Total Day Center attendees: {numParticipants}
        </div>
      )} */}
      {!appointments && <Loader />}
      {!!appointments && numParticipants === 0 && (
        <TypographySubtle>No day center appointments.</TypographySubtle>
      )}
      {selectedDate && (
        <div className="flex flex-col">
          {participants?.map((participant) => {
            return (
              <DayCenterParticipantRow
                key={`${participant.reference.reference}-${selectedDate}`}
                participantReference={participant.reference}
                appointments={appointmentsByParticipantId[participant.id]}
                selectedDate={selectedDate}
              />
            )
          })}
        </div>
      )}
    </div>
  )
}

function DayCenterParticipantRow({
  participantReference,
  appointments,
  selectedDate,
}: {
  participantReference: Reference<Patient> | undefined
  appointments: ExtendedAppointment[]
  selectedDate: DateString
}): JSX.Element {
  const { data: participant } = useRead(Participant, participantReference)
  const { data: allergies } = useSearch(
    ExtendedAllergy,
    ExtendedAllergy.searchQuery({
      participant: participantReference,
    })
  )

  const noteElementId = `note-${participantReference?.reference}`
  const { ref: highlightRef, highlightClassName } = useScrollHighlight(
    useHashMatch(noteElementId)
  )

  const subtext = compact([
    participant?.genderInitial,
    participant?.age,
    participant?.birthDateFormatted,
    participant?.seenMRN && `MRN ${participant.seenMRN}`,
    participant?.languageCodes?.map((code) => code.display).join(', '),
  ]).join(', ')
  return (
    <div
      id={noteElementId}
      ref={highlightRef}
      className={cn('border-t py-3 pl-3', highlightClassName)}
    >
      <div className="flex gap-3">
        <Avatar profile={participantReference} />
        <div className="flex flex-1 flex-col gap-3">
          <div className="flex flex-col gap-1">
            <div className="flex flex-wrap items-center gap-1 text-sm">
              {participant && (
                <TextLink to={participant.relativeUrl}>
                  <PatientName participant={participant} />
                </TextLink>
              )}
              <TypographySubtle>
                <DotSeparator />
              </TypographySubtle>
              <TypographySubtle className="text-foreground-secondary">
                {subtext}
              </TypographySubtle>
              {participant?.flags?.map((flag) => (
                <ParticipantFlagBadge key={flag.coding.code} flag={flag} />
              ))}
              {allergies?.map((allergy) => (
                <Badge
                  key={allergy.id}
                  variant="outline"
                  className="flex justify-center"
                >
                  <IconVirus className="size-3" />
                  {allergy.name}
                </Badge>
              ))}
            </div>
            {appointments.map((appointment) => (
              <AppointmentDisplay
                key={appointment.id}
                appointment={appointment}
              />
            ))}
          </div>
          <DayCenterDayNote
            participantReference={participantReference}
            selectedDate={selectedDate}
          />
        </div>
      </div>
    </div>
  )
}
